import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { SlideTransition } from '../../Transition/Transition';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { toggleDialog } from '../../../store/control/duck';
import { getClientTracking } from '../utils';
import _ from 'lodash';
import { deleteClient } from '../../../store/client/duck';
import { sanitize } from 'dompurify';

const DeleteClientDialog = props => {
  const { intl, userDetails, deleteClientDialog, toggleDialog, deleteClient } = props;

  const { client, closed } = deleteClientDialog || {};

  const close = () => toggleDialog('deleteClient', { ...deleteClientDialog, closed: true });
  const onClosed = () => toggleDialog('deleteClient', false);

  if (!client) {
    return null;
  }

  // What the warning must say is decided by WHERE the record lives, not by the
  // connection status. `inactiveUser` records (未開戶, and a 等待中 client who
  // has only been invited) are deleted outright by the server — user row,
  // factsheet, policies and all. Every other status is an account the client
  // owns: deleting only unlinks this adviser and removes their own factsheet.
  //
  // Keying this on `getClientConnectionStatus(client) === 'Inactive'` — as it
  // used to — got 等待中 wrong in the dangerous direction: an invited inactive
  // client reads as 'Pending', so the dialog promised the client could still log
  // in while the server was about to delete their whole record.
  const isInactive = !!_.get(client, 'inactiveUser', false);
  // `getClientTracking` resolves the adviser's `advisorDetails.trackingId`,
  // which on an ACTIVE client is an unpopulated ObjectId — `isActive` cannot be
  // read from it, so this notice never appeared for a 已連接 / 等待中 / 已中斷
  // client even though their tracking was about to go. The root `trackingId` is
  // the same adviser-scoped record, already populated by the server
  // (formatClientData), so read that for the WARNING.
  const tracking = _.get(client, 'trackingId') || getClientTracking(client, userDetails);
  const isTrackingActive = _.get(tracking, 'isActive', false);

  const confirm = () => {
    // Tracking is deleted server-side as part of deleteClient, along with the
    // rest of this adviser's records for the client. Firing enableTracking
    // 'delete' here as well raced it: the two requests fought over the same
    // Tracking document and the same advisorDetails entry, and if the delete
    // then failed the tracking was gone anyway — silently, with the client
    // still in the list.
    deleteClient({ clientId: _.get(client, '_id', '') });

    close();
  };

  return (
    <Dialog
      className="delete-client-dialog"
      open={!!deleteClientDialog && !closed}
      onClose={close}
      fullWidth={true}
      slots={{
        transition: SlideTransition
      }}
      slotProps={{
        transition: {
          onExited: onClosed
        }
      }}
    >
      <DialogTitle>{intl.formatMessage({ id: 'Delete User' })}</DialogTitle>
      <DialogContent>
        <div
          dangerouslySetInnerHTML={{
            __html: sanitize(intl.formatMessage({ id: isInactive ? 'delete_active_user_message' : 'delete_all_data' }))
          }}
        />

        {isTrackingActive && (
          <div dangerouslySetInnerHTML={{ __html: intl.formatMessage({ id: 'delete_the_tracking' }) }} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} color="primary">
          {intl.formatMessage({ id: 'Cancel' })}
        </Button>
        <Button onClick={confirm} color="primary" autoFocus>
          {intl.formatMessage({ id: 'OK' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const container = connect(
  state => ({
    userDetails: state.user.userDetails,
    deleteClientDialog: state.control.deleteClientDialog
  }),
  {
    toggleDialog,
    deleteClient
  }
)(DeleteClientDialog);

export default injectIntl(container);
